import { cloneElement, useEffect } from "react";
import { Box, Flex } from "@chakra-ui/react";
import { useTopBarHeight } from "../../hooks/userTopBarHeight";
import { useIsSmallBreakpoint } from "../../utils/useBreakpoints";
import { SLIDE_IN_MENUS, Z_INDEX_NAVBAR, Z_INDEX_SLIDE_IN_MENU } from "../../screens/root/constants";
import { SLIDE_IN_ANIMATION_DURATION_MS } from "./constants";
import { menus } from "./menus";

interface SlideInMenuSidebarProps {
  active: boolean;
  isTopBarHidden?: boolean;
  openMenu?: SLIDE_IN_MENUS;
  onCloseMenu: () => void;
}

/**
 * Sidebar slide-in menu for reader contexts.
 * Animates from right to left (translateX) and displays as a sidebar panel.
 */
export function SlideInMenuSidebar({
  active,
  isTopBarHidden = false,
  openMenu,
  onCloseMenu,
}: SlideInMenuSidebarProps) {
  const topBarHeight = useTopBarHeight();
  const isSmallBreakpoint = useIsSmallBreakpoint();

  // Only handle escape key when active
  const effectiveOpenMenu = active ? openMenu : undefined;

  // Escape key handler
  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === "Escape" && effectiveOpenMenu) {
        event.preventDefault();
        event.stopPropagation();
        onCloseMenu();
      }
    };

    if (effectiveOpenMenu) {
      document.addEventListener("keydown", handleEscapeKey, true);
    }

    return () => {
      document.removeEventListener("keydown", handleEscapeKey, true);
    };
  }, [effectiveOpenMenu, onCloseMenu]);

  // When not active, hide instantly without animation
  if (!active) {
    return null;
  }

  return (
    <Box
      id="slideInMenuSidebar"
      position="fixed"
      width={isSmallBreakpoint ? "100%" : "400px"}
      maxWidth="100%"
      height={
        isSmallBreakpoint ? "100dvh" : `calc(100% - ${topBarHeight.get()}px)`
      }
      background="transparent"
      zIndex={isSmallBreakpoint ? Z_INDEX_NAVBAR + 1 : Z_INDEX_SLIDE_IN_MENU}
      right="0"
      top={
        isSmallBreakpoint ? 0 : isTopBarHidden ? 0 : topBarHeight.get() + "px"
      }
      transform={
        openMenu
          ? "translateX(0)"
          : `translateX(${isSmallBreakpoint ? "100%" : "400px"})`
      }
      transition={`transform ${SLIDE_IN_ANIMATION_DURATION_MS}ms ease-out, top ${SLIDE_IN_ANIMATION_DURATION_MS}ms ease-out, height ${SLIDE_IN_ANIMATION_DURATION_MS}ms ease-out`}
      pointerEvents={openMenu ? "auto" : "none"}
    >
      <Box
        height={isSmallBreakpoint ? "100%" : "calc(100% - 20px)"}
        onClick={(e) => e.stopPropagation()}
        position="relative"
        maxWidth="inherit"
        margin={isSmallBreakpoint ? "0px" : "10px"}
        background="charcoal.charcoal"
        backdropFilter="blur(12px)"
        borderRadius={isSmallBreakpoint ? "0px" : "16px"}
        border="1px solid var(--White-Transparency-White-20, rgba(255, 255, 255, 0.20))"
      >
        <Flex
          id="slideInMenuScrollContainer"
          flexDirection="column"
          width="100%"
          height="100%"
        >
          {openMenu && cloneElement(menus[openMenu], { onCloseMenu })}
        </Flex>
      </Box>
    </Box>
  );
}
